import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, Search, Loader2, Edit, Trash2, Shield, 
  KeyRound, Mail, Building2, Calendar, UserPlus,
  MoreHorizontal, Eye
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface User {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  org_id: string | null;
  created_at: string;
  org?: {
    id: string;
    name: string;
  } | null;
  roles?: { role: string; org_id: string | null }[];
}

interface Organization {
  id: string;
  name: string;
}

export function UserManagementPanel() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    org_id: "",
    role: "owner" as "owner" | "admin" | "manager" | "rep" | "client",
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(
        users.filter((user) =>
          user.email.toLowerCase().includes(query) ||
          (user.full_name?.toLowerCase() || "").includes(query) ||
          (user.org?.name?.toLowerCase() || "").includes(query) ||
          user.roles?.some((r) => r.role.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, users]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load all users with org info
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Load all organizations
      const { data: orgs, error: orgsError } = await supabase
        .from("organizations")
        .select("id, name")
        .order("name");

      if (orgsError) throw orgsError;
      setOrganizations(orgs || []);

      // Map org info to users and load roles
      const orgMap = new Map((orgs || []).map((o) => [o.id, o]));
      
      const usersWithDetails = await Promise.all(
        (profiles || []).map(async (profile) => {
          const { data: roles } = await supabase
            .from("user_roles")
            .select("role, org_id")
            .eq("user_id", profile.id);

          return {
            ...profile,
            org: profile.org_id ? orgMap.get(profile.org_id) || null : null,
            roles: roles || [],
          };
        })
      );

      setUsers(usersWithDetails);
      setFilteredUsers(usersWithDetails);
    } catch (error: any) {
      console.error("Failed to load users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    const orgRole = user.roles?.find((r) => r.org_id === user.org_id);
    setEditFormData({
      full_name: user.full_name || "",
      email: user.email,
      phone: user.phone || "",
      org_id: user.org_id || "",
      role: (orgRole?.role as any) || "owner",
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);

      // Update profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: editFormData.full_name,
          email: editFormData.email,
          phone: editFormData.phone || null,
          org_id: editFormData.org_id || null,
        })
        .eq("id", selectedUser.id);

      if (profileError) throw profileError;

      // Update role if org is set
      if (editFormData.org_id) {
        // Delete old roles for this org
        await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", selectedUser.id)
          .eq("org_id", editFormData.org_id);

        // Insert new role
        await supabase.from("user_roles").insert({
          user_id: selectedUser.id,
          org_id: editFormData.org_id,
          role: editFormData.role as any,
        });
      }

      toast.success("User updated successfully");
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      toast.error("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!confirm(`Send password reset email to ${user.email}?`)) return;

    try {
      const { data, error } = await supabase.functions.invoke("admin-reset-password", {
        body: { userEmail: user.email },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Password reset email sent to ${user.email}`);
    } catch (error: any) {
      console.error("Failed to reset password:", error);
      toast.error(`Failed to send reset email: ${error.message}`);
    }
  };

  const handleRemoveFromOrg = async (user: User) => {
    if (!user.org_id) return;
    if (!confirm(`Remove ${user.full_name || user.email} from their organization?`)) return;

    try {
      await supabase
        .from("profiles")
        .update({ org_id: null })
        .eq("id", user.id);

      await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", user.id)
        .eq("org_id", user.org_id);

      toast.success("User removed from organization");
      loadData();
    } catch (error: any) {
      console.error("Failed to remove user:", error);
      toast.error("Failed to remove user");
    }
  };

  const getRoleBadge = (roles: User["roles"]) => {
    if (!roles || roles.length === 0) {
      return <Badge variant="outline">No role</Badge>;
    }

    // Check for platform owner
    const platformOwner = roles.find((r) => r.role === "owner" && r.org_id === null);
    if (platformOwner) {
      return <Badge variant="default" className="bg-purple-600">Platform Owner</Badge>;
    }

    // Get org roles
    const orgRoles = roles.filter((r) => r.org_id !== null);
    if (orgRoles.length === 0) {
      return <Badge variant="outline">No role</Badge>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {orgRoles.map((r, i) => (
          <Badge key={i} variant="secondary">
            {r.role}
          </Badge>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              All Platform Users
            </CardTitle>
            <CardDescription>
              {users.length} total users across all organizations
            </CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, org, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {searchQuery && (
            <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")}>
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {searchQuery ? `No users found matching "${searchQuery}"` : "No users found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.full_name || "N/A"}</span>
                        <span className="text-sm text-muted-foreground">{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.org ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{user.org.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No org</span>
                      )}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.roles)}</TableCell>
                    <TableCell>
                      {user.phone || <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(user.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          {user.org_id && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleRemoveFromOrg(user)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove from Org
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and organization assignment
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select
                  value={editFormData.org_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, org_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No organization</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editFormData.org_id && (
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={editFormData.role}
                    onValueChange={(value: any) => setEditFormData({ ...editFormData, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="rep">Rep</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
